import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";

/**
 * Reusable modal for bulk actions with two choices (e.g. Whitelist/Un-whitelist, Deactivate/Reactivate).
 * Shows instruction, two option buttons, optional description, scrollable user list with status, and Cancel/Confirm.
 */
export default function BulkActionChoiceModal({
  open,
  onClose,
  title,
  options,
  value,
  onValueChange,
  selectedUserIds,
  companyUsers,
  getStatusLabel,
  onConfirm,
  submitting,
  currentUserProcoreId,
}) {
  const count = selectedUserIds?.length ?? 0;
  const selectedOption = options.find((o) => o.value === value);
  const orderedSelectedUserIds =
    value === "deactivate" && currentUserProcoreId
      ? [
          ...(selectedUserIds || []).filter((uid) => uid === currentUserProcoreId),
          ...(selectedUserIds || []).filter((uid) => uid !== currentUserProcoreId),
        ]
      : selectedUserIds || [];

  const handleConfirm = async () => {
    if (!value) return;
    await onConfirm();
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Typography sx={{ mb: 2 }}>
          Choose the action to apply to the{" "}
          <strong>
            {count} user{count === 1 ? "" : "s"}
          </strong>{" "}
          selected.
        </Typography>
        <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mt: 2 }}>
          {options.map((opt) => (
            <Button
              key={opt.value}
              variant={value === opt.value ? "contained" : "outlined"}
              color={opt.color}
              onClick={() => onValueChange(opt.value)}
            >
              {opt.label}
            </Button>
          ))}
        </Box>
        {selectedOption?.description && (
          <Typography variant="body2" sx={{ mt: 2, color: "text.secondary" }}>
            {selectedOption.description}
          </Typography>
        )}
        <Box
          sx={{
            maxHeight: 180,
            overflowY: "auto",
            border: "1px solid #e0e0e0",
            borderRadius: 1,
            p: 1,
            mt: 2,
          }}
        >
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {orderedSelectedUserIds.map((uid) => {
              const u = companyUsers?.find((usr) => usr.id === uid);
              const statusLabel = u && value ? getStatusLabel(u, value) : null;
              const isOwnUser = value === "deactivate" && uid === currentUserProcoreId;
              return (
                <li key={uid} style={{ fontWeight: isOwnUser ? 700 : 400 }}>
                  {u?.name || "Unknown User"}
                  {isOwnUser && (
                    <span style={{ marginLeft: 6, fontSize: "0.85em" }}>[your own user]</span>
                  )}
                  {statusLabel && (
                    <span style={{ color: "gray", marginLeft: 6, fontSize: "0.85em" }}>
                      {statusLabel}
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          color={selectedOption?.color ?? "primary"}
          disabled={!value}
          onClick={handleConfirm}
        >
          {submitting ? <CircularProgress size={24} /> : (selectedOption?.label ?? "Confirm")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
